import type { MovementObstacle } from "@frontier/simulation/movement";
import type { Point } from "./geometry";

export interface NavigationGeometry {
  bounds: { x: number; y: number; width: number; height: number };
  obstacles: readonly MovementObstacle[];
  radius: number;
}
export type NavigationPathResult =
  | { status: "searching" }
  | { status: "found"; destination: Point; path: Point[] }
  | { status: "blocked" };

export const NAVIGATION_CLEARANCE = 4;
export const NAVIGATION_FRAME_BUDGET_MS = 2;
const EPSILON = 1e-7;
const SAMPLES = 32;
const MAX_PROJECTION = 256;
const MAX_OBSTACLES = 128;
const MAX_NODES = 2048;
const MAX_TOTAL_CHECKS = 1_000_000;
const MAX_ADVANCE_CHECKS = 4096;
const SEARCHING: NavigationPathResult = { status: "searching" };
const BLOCKED: NavigationPathResult = { status: "blocked" };
type Task<T> = Generator<void, T>;
interface Node {
  point: Point;
  cost: number;
  parent: number;
  closed: boolean;
  goal: boolean;
}

function validPoint(point: Point): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}
function validGeometry(geometry: NavigationGeometry): boolean {
  const { bounds, radius } = geometry;
  return (
    validPoint(bounds) &&
    Number.isFinite(radius) &&
    radius >= 0 &&
    Number.isFinite(bounds.width) &&
    Number.isFinite(bounds.height) &&
    Number.isFinite(bounds.x + bounds.width) &&
    Number.isFinite(bounds.y + bounds.height) &&
    bounds.width > radius * 2 &&
    bounds.height > radius * 2
  );
}
function validObstacle(obstacle: MovementObstacle): boolean {
  return (
    validPoint(obstacle) &&
    Number.isFinite(obstacle.radius) &&
    obstacle.radius >= 0
  );
}
function inside(point: Point, geometry: NavigationGeometry): boolean {
  const { bounds, radius } = geometry;
  return (
    validPoint(point) &&
    point.x >= bounds.x + radius - EPSILON &&
    point.y >= bounds.y + radius - EPSILON &&
    point.x <= bounds.x + bounds.width - radius + EPSILON &&
    point.y <= bounds.y + bounds.height - radius + EPSILON
  );
}
function relevant(
  obstacle: MovementObstacle,
  geometry: NavigationGeometry,
): boolean {
  const { bounds, radius } = geometry;
  const reach = obstacle.radius + radius + NAVIGATION_CLEARANCE;
  return (
    obstacle.x + reach >= bounds.x &&
    obstacle.y + reach >= bounds.y &&
    obstacle.x - reach <= bounds.x + bounds.width &&
    obstacle.y - reach <= bounds.y + bounds.height
  );
}
function obstructs(
  start: Point,
  end: Point,
  obstacle: MovementObstacle,
  radius: number,
): boolean {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const ox = start.x - obstacle.x;
  const oy = start.y - obstacle.y;
  const physical = obstacle.radius + radius;
  const padded = physical + NAVIGATION_CLEARANCE;
  const length = dx * dx + dy * dy;
  const outward = ox * dx + oy * dy;
  const startDistance = ox * ox + oy * oy;
  const endDistance = (end.x - obstacle.x) ** 2 + (end.y - obstacle.y) ** 2;
  // A ship resting against the server collider may leave the extra navigation margin.
  if (
    startDistance >= physical * physical - EPSILON &&
    startDistance < padded * padded &&
    outward >= 0 &&
    endDistance >= padded * padded - EPSILON
  )
    return false;
  const t = length === 0 ? 0 : Math.max(0, Math.min(1, -outward / length));
  return (ox + dx * t) ** 2 + (oy + dy * t) ** 2 < padded * padded - EPSILON;
}

export function navigationSegmentClear(
  start: Point,
  end: Point,
  geometry: NavigationGeometry,
): boolean {
  if (
    !validGeometry(geometry) ||
    !inside(start, geometry) ||
    !inside(end, geometry)
  )
    return false;
  for (const obstacle of geometry.obstacles) {
    if (!validObstacle(obstacle)) return false;
    if (
      relevant(obstacle, geometry) &&
      obstructs(start, end, obstacle, geometry.radius)
    )
      return false;
  }
  return true;
}

/** Each yield charges one construction, node-scan or individual circle check. */
export class NavigationPathSearch {
  private readonly task: Task<NavigationPathResult>;
  private readonly start: Point;
  private readonly destination: Point;
  private readonly geometry: NavigationGeometry;
  private checks = 0;
  private result: NavigationPathResult = SEARCHING;

  constructor(start: Point, destination: Point, geometry: NavigationGeometry) {
    this.start = { ...start };
    this.destination = { ...destination };
    this.geometry = {
      bounds: { ...geometry.bounds },
      radius: geometry.radius,
      obstacles: geometry.obstacles,
    };
    this.task = this.search();
  }

  advance(maxChecks = 1024): NavigationPathResult {
    if (this.result.status !== "searching") return this.result;
    if (!Number.isFinite(maxChecks) || maxChecks < 1)
      return (this.result = BLOCKED);
    const budget = Math.min(MAX_ADVANCE_CHECKS, Math.floor(maxChecks));
    for (let index = 0; index < budget; index++) {
      if (++this.checks > MAX_TOTAL_CHECKS) return (this.result = BLOCKED);
      const step = this.task.next();
      if (step.done) return (this.result = step.value);
    }
    return SEARCHING;
  }

  advanceFrame(): NavigationPathResult {
    const deadline = performance.now() + NAVIGATION_FRAME_BUDGET_MS;
    let result: NavigationPathResult;
    do {
      result = this.advance(MAX_ADVANCE_CHECKS);
    } while (result.status === "searching" && performance.now() < deadline);
    return result;
  }

  private *clear(
    start: Point,
    end: Point,
    geometry: NavigationGeometry,
  ): Task<boolean> {
    yield;
    if (!inside(start, geometry) || !inside(end, geometry)) return false;
    for (const obstacle of geometry.obstacles) {
      yield;
      if (obstructs(start, end, obstacle, geometry.radius)) return false;
    }
    return true;
  }

  private *free(
    point: Point,
    geometry: NavigationGeometry,
    padding = NAVIGATION_CLEARANCE,
  ): Task<boolean> {
    yield;
    if (!inside(point, geometry)) return false;
    for (const obstacle of geometry.obstacles) {
      yield;
      const radius = obstacle.radius + geometry.radius + padding;
      if (
        (point.x - obstacle.x) ** 2 + (point.y - obstacle.y) ** 2 <
        radius * radius - EPSILON
      )
        return false;
    }
    return true;
  }

  private *search(): Task<NavigationPathResult> {
    yield;
    const original = this.geometry;
    if (
      !validGeometry(original) ||
      !validPoint(this.start) ||
      !validPoint(this.destination)
    )
      return BLOCKED;
    const { bounds, radius } = original;
    if (
      this.destination.x < bounds.x ||
      this.destination.y < bounds.y ||
      this.destination.x > bounds.x + bounds.width ||
      this.destination.y > bounds.y + bounds.height
    )
      return BLOCKED;
    const obstacles: MovementObstacle[] = [];
    for (const obstacle of original.obstacles) {
      yield;
      if (!validObstacle(obstacle)) return BLOCKED;
      if (relevant(obstacle, original))
        obstacles.push({
          id: obstacle.id,
          x: obstacle.x,
          y: obstacle.y,
          radius: obstacle.radius,
          ...(obstacle.solid === undefined ? {} : { solid: obstacle.solid }),
        });
      if (obstacles.length > MAX_OBSTACLES) return BLOCKED;
    }
    const geometry = { bounds, radius, obstacles };
    if (!(yield* this.free(this.start, geometry, 0))) return BLOCKED;
    const requested = {
      x: Math.max(
        bounds.x + radius,
        Math.min(bounds.x + bounds.width - radius, this.destination.x),
      ),
      y: Math.max(
        bounds.y + radius,
        Math.min(bounds.y + bounds.height - radius, this.destination.y),
      ),
    };
    const nodes: Node[] = [
      { point: this.start, cost: 0, parent: -1, closed: false, goal: false },
    ];
    const keys = new Map<string, number>();
    keys.set(this.key(this.start), 0);
    let closestGoal = -1;
    let closestDistance = Infinity;
    const add = (point: Point, goal: boolean): void => {
      const key = this.key(point);
      let index = keys.get(key);
      if (index === undefined) {
        index = nodes.length;
        keys.set(key, index);
        nodes.push({ point, cost: Infinity, parent: -1, closed: false, goal });
      } else if (goal) {
        const node = nodes[index];
        if (node) node.goal = true;
      }
      const distance = Math.hypot(point.x - requested.x, point.y - requested.y);
      if (goal && distance < closestDistance) {
        closestDistance = distance;
        closestGoal = index;
      }
    };
    const exact = yield* this.free(requested, geometry);
    if (exact) add(requested, true);
    else {
      for (const obstacle of obstacles) {
        yield;
        const padded = obstacle.radius + radius + NAVIGATION_CLEARANCE + 0.01;
        if (
          Math.hypot(requested.x - obstacle.x, requested.y - obstacle.y) >
          padded
        )
          continue;
        const angle =
          requested.x !== obstacle.x || requested.y !== obstacle.y
            ? Math.atan2(requested.y - obstacle.y, requested.x - obstacle.x)
            : Math.atan2(this.start.y - obstacle.y, this.start.x - obstacle.x);
        for (let index = 0; index < SAMPLES; index++) {
          yield;
          const direction = angle + (index * Math.PI * 2) / SAMPLES;
          const point = {
            x: obstacle.x + Math.cos(direction) * padded,
            y: obstacle.y + Math.sin(direction) * padded,
          };
          if (
            Math.hypot(point.x - requested.x, point.y - requested.y) <=
              MAX_PROJECTION &&
            (yield* this.free(point, geometry))
          )
            add(point, true);
        }
      }
      for (const distance of [16, 32, 64, 128, 256]) {
        for (let index = 0; index < SAMPLES; index++) {
          yield;
          const angle = (index * Math.PI * 2) / SAMPLES;
          const point = {
            x: requested.x + Math.cos(angle) * distance,
            y: requested.y + Math.sin(angle) * distance,
          };
          if (yield* this.free(point, geometry)) add(point, true);
        }
      }
    }
    const target = nodes[closestGoal];
    if (!target || nodes.length > MAX_NODES) return BLOCKED;
    if (yield* this.clear(this.start, target.point, geometry))
      return {
        status: "found",
        destination: { ...target.point },
        path: [{ ...target.point }],
      };
    for (const obstacle of obstacles) {
      const distance =
        (obstacle.radius + radius + NAVIGATION_CLEARANCE) /
          Math.cos(Math.PI / SAMPLES) +
        0.5;
      for (let index = 0; index < SAMPLES; index++) {
        yield;
        const angle = (index * Math.PI * 2) / SAMPLES;
        const point = {
          x: obstacle.x + Math.cos(angle) * distance,
          y: obstacle.y + Math.sin(angle) * distance,
        };
        if (yield* this.free(point, geometry)) add(point, false);
        if (nodes.length > MAX_NODES) return BLOCKED;
      }
    }
    while (true) {
      let current = -1;
      let score = Infinity;
      for (let index = 0; index < nodes.length; index++) {
        yield;
        const node = nodes[index];
        if (!node || node.closed || !Number.isFinite(node.cost)) continue;
        const candidate =
          node.cost +
          Math.hypot(
            node.point.x - target.point.x,
            node.point.y - target.point.y,
          );
        if (candidate < score) {
          current = index;
          score = candidate;
        }
      }
      if (current === closestGoal) return yield* this.finish(nodes, current);
      if (current === -1) break;
      const from = nodes[current];
      if (!from) return BLOCKED;
      from.closed = true;
      for (let index = 0; index < nodes.length; index++) {
        yield;
        const to = nodes[index];
        if (!to || to.closed) continue;
        const cost =
          from.cost +
          Math.hypot(from.point.x - to.point.x, from.point.y - to.point.y);
        if (
          cost >= to.cost ||
          !(yield* this.clear(from.point, to.point, geometry))
        )
          continue;
        to.cost = cost;
        to.parent = current;
      }
    }
    let reachable = -1;
    let distance = Infinity;
    for (let index = 0; index < nodes.length; index++) {
      yield;
      const node = nodes[index];
      if (!node?.goal || !Number.isFinite(node.cost)) continue;
      const candidate = Math.hypot(
        node.point.x - requested.x,
        node.point.y - requested.y,
      );
      if (candidate < distance) {
        reachable = index;
        distance = candidate;
      }
    }
    return reachable < 0 ? BLOCKED : yield* this.finish(nodes, reachable);
  }

  private key(point: Point): string {
    return `${point.x.toFixed(6)},${point.y.toFixed(6)}`;
  }

  private *finish(nodes: Node[], goal: number): Task<NavigationPathResult> {
    const destination = nodes[goal]?.point;
    if (!destination) return BLOCKED;
    const reverse: Point[] = [];
    for (let index = goal; index > 0;) {
      yield;
      const node = nodes[index];
      if (!node || node.parent < 0) return BLOCKED;
      reverse.push(node.point);
      index = node.parent;
    }
    const path: Point[] = [];
    for (let index = reverse.length - 1; index >= 0; index--) {
      yield;
      const point = reverse[index];
      if (point) path.push({ ...point });
    }
    if (!path.length) path.push({ ...destination });
    return { status: "found", destination: { ...destination }, path };
  }
}
